import type { GridCellParams } from '@mui/x-data-grid-pro';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import OptionalRowInput from './OptionalRowInput';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import UpDownArrowIcons from './UpDownArrowIcons';
import { getIsCellEditingAllowed } from '../tableUtils/customCellHelpers';
import { isNumericColumn } from '../helper';
import { isGeneratedRow } from '../../../../grid/reduxStore/crmRowMarkers';

export const getRoundedValueTooltipTitle = ({
  value,
  numberOfDecimalPlaces,
}: {
  value: number | string;
  numberOfDecimalPlaces?: number;
}): string => {
  const numValue = Number(value);
  const actualDecimalPlaces = numValue.toString().includes('.') ? numValue.toString().split('.')[1].length : 0;
  const isRounded =
    numberOfDecimalPlaces &&
    (actualDecimalPlaces > numberOfDecimalPlaces || (numberOfDecimalPlaces === 5 && actualDecimalPlaces === numberOfDecimalPlaces));

  const shouldShowTooltip = value !== undefined && value !== null && !isNaN(numValue) && isRounded;

  return shouldShowTooltip ? `Number rounded from ${value}` : '';
};

export const stripHtmlTags = (str: string): string => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '');
};

export const CustomTableCell = ({
  params,
  value,
  numberOfDecimalPlaces,
}: {
  params: GridCellParams;
  value: string;
  numberOfDecimalPlaces?: number;
}) => {
  if (!params) {
    return <></>;
  }

  const rowType = params?.row?.rowType?.toLowerCase();
  const columnType = params?.colDef?.['columnType'] ? params.colDef['columnType'].toString().toLowerCase() : '';
  const isNumericCellValue = isNumericColumn(params?.colDef?.['columnType']);
  const isRenderInputActive =
    params.colDef['columnType'] === ColumnTypeIdentifier.OPTIONAL && params.row.rowType === RowTypesIdentifier.BODY;
  const valueDisplay = stripHtmlTags(value) || '';
  const isRowOptional = params?.row?.isRowOptional ?? false;
  const isOptionalToggleAvailable = params?.row?.isOptionalToggleAvailable && isRenderInputActive;
  const tooltipTitle = getRoundedValueTooltipTitle({ value: params.value as number, numberOfDecimalPlaces });
  const isCellEditingAllowed = getIsCellEditingAllowed({ row: params?.row, colDef: params?.colDef });
  return (
    <Tooltip title={tooltipTitle} placement="bottom" followCursor data-testid="render-cell-tooltip">
      <Box
        component="span"
        className={isNumericCellValue ? 'py-numeric-cell-value' : undefined}
        display="flex"
        alignItems="center"
        gap="3px"
        data-testid="render-cell-test"
        data-testid-row-type={rowType}
        data-testid-column-type={columnType}
        height="100%"
      >
        {isOptionalToggleAvailable ? (
          <OptionalRowInput isRowOptional={isRowOptional} id={params.id as string} disabled={isGeneratedRow(params.row)} />
        ) : (
          valueDisplay
        )}
        {isCellEditingAllowed && <UpDownArrowIcons />}
      </Box>
    </Tooltip>
  );
};

export const CustomTableCellInPreview = ({
  params,
  value,
  numberOfDecimalPlaces,
}: {
  params: any;
  value: string;
  children?: React.ReactNode;
  numberOfDecimalPlaces?: number;
}) => {
  const rowType = params?.row?.rowType?.toLowerCase();
  const columnType = params?.colDef?.columnType?.toString().toLowerCase();
  const isNumericCellValue = isNumericColumn(params?.colDef?.columnType);
  const valueDisplay = value || '';
  const tooltipTitle = getRoundedValueTooltipTitle({ value: params.value, numberOfDecimalPlaces });
  return (
    <Tooltip title={tooltipTitle} placement="bottom" followCursor data-testid="render-cell-tooltip">
      <Box
        component="span"
        className={isNumericCellValue ? 'py-numeric-cell-value' : undefined}
        display="flex"
        alignItems="center"
        height="100%"
        data-testid="render-cell-test-preview"
        data-testid-row-type={rowType}
        data-testid-column-type={columnType}
      >
        {valueDisplay}
      </Box>
    </Tooltip>
  );
};
